import { apiClient } from "@/lib/api/client";
import { buildQueryString } from "@/lib/utils/query-string";
import type {
  Address,
  Contact,
  ContactListParams,
  ContactRole,
  CreateAddressPayload,
  CreateContactPayload,
  CreateCustomerAccountPayload,
  CreatePhoneNumberPayload,
  CreateSocialLinkPayload,
  CustomerAccount,
  CustomerAccountListItem,
  ListParams,
  PaginatedResponse,
  PhoneNumber,
  SocialLink,
  UpdateAddressPayload,
  UpdateContactPayload,
  UpdateCustomerAccountPayload,
  UpdatePhoneNumberPayload,
  UpdateSocialLinkPayload,
  UpsertContactRolePayload,
} from "../types/customer.types";

const BASE = "/api/customers/";

// ─── Customer accounts ───────────────────────────────────────────────────

export async function listCustomerAccounts(
  params: ListParams = {},
): Promise<PaginatedResponse<CustomerAccountListItem>> {
  const qs = buildQueryString(params as Record<string, unknown>);
  const { data } = await apiClient.get(`${BASE}accounts/?${qs}`);
  return data;
}

export async function getCustomerAccount(id: number): Promise<CustomerAccount> {
  const { data } = await apiClient.get(`${BASE}accounts/${id}/`);
  return data;
}

export async function createCustomerAccount(
  payload: CreateCustomerAccountPayload,
): Promise<CustomerAccount> {
  const { data } = await apiClient.post(`${BASE}accounts/`, payload);
  return data;
}

export async function updateCustomerAccount(
  id: number,
  payload: UpdateCustomerAccountPayload,
): Promise<CustomerAccount> {
  const { data } = await apiClient.patch(`${BASE}accounts/${id}/`, payload);
  return data;
}

export async function deleteCustomerAccount(id: number): Promise<void> {
  await apiClient.delete(`${BASE}accounts/${id}/`);
}

export async function listCustomerAccountContacts(id: number): Promise<Contact[]> {
  const { data } = await apiClient.get(`${BASE}accounts/${id}/contacts/`);
  return data;
}

export async function setPrimaryContact(
  accountId: number,
  contactId: number,
): Promise<CustomerAccount> {
  const { data } = await apiClient.post(
    `${BASE}accounts/${accountId}/set-primary-contact/`,
    { contact_id: contactId },
  );
  return data;
}

// ─── Contacts ────────────────────────────────────────────────────────────

export async function listContacts(
  params: ContactListParams = {},
): Promise<PaginatedResponse<Contact>> {
  const qs = buildQueryString(params as Record<string, unknown>);
  const { data } = await apiClient.get(`${BASE}contacts/?${qs}`);
  return data;
}

export async function getContact(id: number): Promise<Contact> {
  const { data } = await apiClient.get(`${BASE}contacts/${id}/`);
  return data;
}

export async function createContact(payload: CreateContactPayload): Promise<Contact> {
  const { data } = await apiClient.post(`${BASE}contacts/`, payload);
  return data;
}

export async function updateContact(
  id: number,
  payload: UpdateContactPayload,
): Promise<Contact> {
  const { data } = await apiClient.patch(`${BASE}contacts/${id}/`, payload);
  return data;
}

export async function deleteContact(id: number): Promise<void> {
  await apiClient.delete(`${BASE}contacts/${id}/`);
}

export async function reassignContact(
  contactId: number,
  customerAccountId: number | null,
): Promise<Contact> {
  const { data } = await apiClient.post(`${BASE}contacts/${contactId}/reassign/`, {
    customer_account_id: customerAccountId,
  });
  return data;
}

// ─── Contact roles ───────────────────────────────────────────────────────

export async function listContactRoles(activeOnly = false): Promise<ContactRole[]> {
  const qs = activeOnly ? "?active_only=true" : "";
  const { data } = await apiClient.get(`${BASE}roles/${qs}`);
  return data;
}

export async function createContactRole(
  payload: UpsertContactRolePayload,
): Promise<ContactRole> {
  const { data } = await apiClient.post(`${BASE}roles/`, payload);
  return data;
}

export async function updateContactRole(
  id: number,
  payload: Partial<UpsertContactRolePayload>,
): Promise<ContactRole> {
  const { data } = await apiClient.patch(`${BASE}roles/${id}/`, payload);
  return data;
}

export async function reorderContactRoles(orderedIds: number[]): Promise<ContactRole[]> {
  const { data } = await apiClient.post(`${BASE}roles/reorder/`, {
    ordered_ids: orderedIds,
  });
  return data;
}

// ─── Addresses ───────────────────────────────────────────────────────────

export async function listAccountAddresses(accountId: number): Promise<Address[]> {
  const { data } = await apiClient.get(`${BASE}accounts/${accountId}/addresses/`);
  return data;
}

export async function createAddress(
  accountId: number,
  payload: CreateAddressPayload,
): Promise<Address> {
  const { data } = await apiClient.post(
    `${BASE}accounts/${accountId}/addresses/`,
    payload,
  );
  return data;
}

export async function updateAddress(
  id: number,
  payload: UpdateAddressPayload,
): Promise<Address> {
  const { data } = await apiClient.patch(`${BASE}addresses/${id}/`, payload);
  return data;
}

export async function deleteAddress(id: number): Promise<void> {
  await apiClient.delete(`${BASE}addresses/${id}/`);
}

export async function setPrimaryAddress(id: number): Promise<Address> {
  const { data } = await apiClient.post(`${BASE}addresses/${id}/set-primary/`);
  return data;
}

// ─── Phone numbers ───────────────────────────────────────────────────────

export async function listAccountPhones(accountId: number): Promise<PhoneNumber[]> {
  const { data } = await apiClient.get(`${BASE}accounts/${accountId}/phones/`);
  return data;
}

export async function listContactPhones(contactId: number): Promise<PhoneNumber[]> {
  const { data } = await apiClient.get(`${BASE}contacts/${contactId}/phones/`);
  return data;
}

export async function createAccountPhone(
  accountId: number,
  payload: CreatePhoneNumberPayload,
): Promise<PhoneNumber> {
  const { data } = await apiClient.post(
    `${BASE}accounts/${accountId}/phones/`,
    payload,
  );
  return data;
}

export async function createContactPhone(
  contactId: number,
  payload: CreatePhoneNumberPayload,
): Promise<PhoneNumber> {
  const { data } = await apiClient.post(
    `${BASE}contacts/${contactId}/phones/`,
    payload,
  );
  return data;
}

export async function updatePhoneNumber(
  id: number,
  payload: UpdatePhoneNumberPayload,
): Promise<PhoneNumber> {
  const { data } = await apiClient.patch(`${BASE}phones/${id}/`, payload);
  return data;
}

export async function deletePhoneNumber(id: number): Promise<void> {
  await apiClient.delete(`${BASE}phones/${id}/`);
}

export async function setPrimaryPhoneNumber(id: number): Promise<PhoneNumber> {
  const { data } = await apiClient.post(`${BASE}phones/${id}/set-primary/`);
  return data;
}

// ─── Social links ────────────────────────────────────────────────────────

export async function listAccountSocialLinks(accountId: number): Promise<SocialLink[]> {
  const { data } = await apiClient.get(`${BASE}accounts/${accountId}/social-links/`);
  return data;
}

export async function listContactSocialLinks(contactId: number): Promise<SocialLink[]> {
  const { data } = await apiClient.get(`${BASE}contacts/${contactId}/social-links/`);
  return data;
}

export async function createAccountSocialLink(
  accountId: number,
  payload: CreateSocialLinkPayload,
): Promise<SocialLink> {
  const { data } = await apiClient.post(
    `${BASE}accounts/${accountId}/social-links/`,
    payload,
  );
  return data;
}

export async function createContactSocialLink(
  contactId: number,
  payload: CreateSocialLinkPayload,
): Promise<SocialLink> {
  const { data } = await apiClient.post(
    `${BASE}contacts/${contactId}/social-links/`,
    payload,
  );
  return data;
}

export async function updateSocialLink(
  id: number,
  payload: UpdateSocialLinkPayload,
): Promise<SocialLink> {
  const { data } = await apiClient.patch(`${BASE}social-links/${id}/`, payload);
  return data;
}

export async function deleteSocialLink(id: number): Promise<void> {
  await apiClient.delete(`${BASE}social-links/${id}/`);
}
